import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { LocalStorageService } from './local-storage.service';
import { LoginService } from './login.service';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { catchError, combineLatest, EMPTY, filter, from, map, of, switchMap, take } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SwitchDivisionService {
    readonly #injector: Injector = inject(Injector);
    readonly #loginService = inject(LoginService);
    readonly #divisionsStore = inject(GlobalDivisionsStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #localStorageService = inject(LocalStorageService);

    private jwtClaims$ = toObservable(this.#localStorageService.jwtClaims, { injector: this.#injector });

    public switchDivision(divisionId: number, divisionCode: string) {
        return this.#loginService.selectDivision(divisionCode).pipe(
            switchMap(() => from(this.#globalLoggedInUserStore.setSelectedIamDivisionId(divisionId))),
            switchMap(() =>
                from(this.#divisionsStore.getAllDivisionData()).pipe(
                    filter(res => res.status === 'success'),
                    map(res => res.value)
                )
            ),
            switchMap(([allIamDivisions, allDivisionConfigs]) =>
                combineLatest([of(allDivisionConfigs), of(allIamDivisions), this.jwtClaims$])
            ),
            filter(([configs, iamDivisions, jwtClaims]) => {
                const configsLoaded = configs?.length > 0 && iamDivisions?.length > 0;
                const jwtMatches = jwtClaims?.recs?.some(rec => rec.divisionCode === divisionCode);
                return !!(configsLoaded && jwtMatches);
            }),
            catchError(err => {
                console.error('Failed to switch division:', err);
                return EMPTY;
            }),
            take(1)
        );
    }
}
